import { DEVICE_ID_PATTERN } from './devices';
import { normalizeJoinCode } from './practices';
import type { PracticeType } from './roles';

/**
 * A patient link: a patient's Curentia account linked to a clinical practice.
 * It lets the practice ask the patient for history, and lets the patient name
 * the practice as a source of records.
 *
 * requested: the practice found the account by mobile number or ABHA and waits for the patient.
 * active: linked, by the patient's one-time code in the clinic or by their approval.
 * declined: the patient said no to a request.
 * removed: the patient or the practice ended the link.
 */
export const PATIENT_LINK_STATUSES = ['requested', 'active', 'declined', 'removed'] as const;
export type PatientLinkStatus = (typeof PATIENT_LINK_STATUSES)[number];

/** How a link was made: the patient's code shown in the clinic, or a request the patient approved. */
export const PATIENT_LINK_METHODS = ['code', 'request'] as const;
export type PatientLinkMethod = (typeof PATIENT_LINK_METHODS)[number];

/** Minutes a patient's link code stays usable. */
export const LINK_CODE_TTL_MINUTES = 15;
/** Days after a patient declines or removes a link before a request from the same practice reaches them again. */
export const LINK_REQUEST_COOLDOWN_DAYS = 7;
/** Most unanswered link requests one practice may have open. */
export const MAX_OPEN_LINK_REQUESTS = 200;

const QR_PREFIX = 'CURENTIA:LINK:';

/**
 * The text in a patient's link QR code: the one-time code and the device
 * showing it. The scanning device checks that the linked account has that
 * device, which confirms the patient's key in person.
 */
export function linkQrText(code: string, deviceId: string): string {
  const normalized = normalizeJoinCode(code);
  if (!normalized || !DEVICE_ID_PATTERN.test(deviceId)) throw new Error('Not a link code and a device ID');
  return `${QR_PREFIX}${normalized}:${deviceId}`;
}

/** Reads a scanned link QR code, or a code typed from the patient's screen. Returns null for anything else. */
export function parseLinkCode(text: string): { code: string; deviceId?: string } | null {
  const trimmed = text.trim();
  if (trimmed.toUpperCase().startsWith(QR_PREFIX)) {
    const [code = '', deviceId = '', ...rest] = trimmed.slice(QR_PREFIX.length).split(':');
    const normalized = normalizeJoinCode(code);
    return normalized && DEVICE_ID_PATTERN.test(deviceId) && rest.length === 0 ? { code: normalized, deviceId } : null;
  }
  const normalized = normalizeJoinCode(trimmed);
  return normalized ? { code: normalized } : null;
}

/** A link as the practice sees it, with the account details its records need. */
export interface PracticePatientLink {
  id: string;
  practiceId: string;
  status: PatientLinkStatus;
  method: PatientLinkMethod;
  patient: { accountId: string; name: string; mobile: string; abhaNumber?: string; abhaAddress?: string };
  /** The patient's active devices, so a scanned code's device can be checked. */
  deviceIds: string[];
  linkedAt: string;
}

/** A link as the patient sees it. */
export interface MyPatientLink {
  id: string;
  status: PatientLinkStatus;
  method: PatientLinkMethod;
  practice: { id: string; name: string; type: PracticeType; city: string };
  /** The member who linked or asked. */
  requestedBy: { name: string };
  createdAt: string;
  decidedAt?: string;
}
